import { Helmet } from "react-helmet-async";

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: "website" | "article";
  structuredData?: object;
}

const defaultSEO = {
  title:
    "Setgo Innovations - Empowering India Through Edutech, Fintech & Jobtech",
  description:
    "Transforming India's youth and micro-business ecosystem through innovation. Making India digitally literate, financially empowered, and job-ready with SetGo, Digistore Pay, and Easy Jobs.",
  keywords:
    "edutech, fintech, jobtech, digital literacy, job opportunities, skill training, digital payments, Kerala startup, India education, micro business, learn and earn",
  image: "/og-image.jpg",
  url: "https://setgo.in",
  type: "website" as const,
};

export function SEO({
  title = defaultSEO.title,
  description = defaultSEO.description,
  keywords = defaultSEO.keywords,
  image = defaultSEO.image,
  url = defaultSEO.url,
  type = defaultSEO.type,
  structuredData,
}: SEOProps) {
  const fullTitle =
    title === defaultSEO.title ? title : `${title} | Setgo Innovations`;
  const fullUrl = url.startsWith("http") ? url : `${defaultSEO.url}${url}`;
  const fullImageUrl = image.startsWith("http")
    ? image
    : `${defaultSEO.url}${image}`;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content="Setgo Innovations Private Limited" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />

      {/* Canonical URL */}
      <link rel="canonical" href={fullUrl} />

      {/* Open Graph Meta Tags */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullImageUrl} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:site_name" content="Setgo Innovations" />
      <meta property="og:locale" content="en_IN" />

      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImageUrl} />
      <meta name="twitter:site" content="@setgoinnovations" />
      <meta name="twitter:creator" content="@setgoinnovations" />

      {/* Additional Meta Tags */}
      <meta name="robots" content="index, follow" />
      <meta name="language" content="English" />
      <meta name="revisit-after" content="7 days" />

      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
}
